/**
 * Which surfaces the sidebar shows: the multi-select that replaced the product
 * dropdown (2026-10-06).
 *
 * A surface is a classic-sidebar module section (Gemini Enterprise, Endpoint
 * Security, ...; `surface: true` in config/nav/catalog.js), listed in the
 * order the sidebar draws them. By default the sidebar shows the surfaces the org is licensed for;
 * unlicensed ones stay in the list, with a lock, so a user can still open one
 * (its pages show their upsell screen). The user's choices are stored as
 * overrides of that default, never as a full list, so a module the org buys
 * later appears without anyone ticking it. One surface always stays shown.
 *
 * Storage contract: localStorage SURFACES_STORAGE_KEY holds JSON
 * { "<section key>": true | false }; SURFACES_EVENT on window announces a change.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PRODUCTS } from '../layout/Sidebar/productConfig';
import { NAV_SECTIONS } from '../../config/nav/catalog';
import useModuleLicense from '../../licensing/useModuleLicense';
import { isFeatureEnabled } from '../../utils/featureFlags';

export const SURFACES_STORAGE_KEY = 'saf3ai.surfaces';
export const SURFACES_EVENT = 'saf3ai:surfaces-changed';

/** Surface section keys, in the order the sidebar draws them (catalog order). */
export const SURFACE_KEYS = Object.freeze(Object.keys(NAV_SECTIONS).filter((key) => NAV_SECTIONS[key].surface));

const flagsPass = (gate) => {
  if (!gate) return true;
  if (Array.isArray(gate)) return gate.every(flagsPass);
  return !gate.feature || isFeatureEnabled(gate.feature);
};

const productFlagPasses = (key) => {
  const product = PRODUCTS.find((p) => p.sections.includes(key));
  return !product?.featureFlag || isFeatureEnabled(product.featureFlag);
};

export const readOverrides = () => {
  try {
    const raw = JSON.parse(window.localStorage.getItem(SURFACES_STORAGE_KEY) || '{}');
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  } catch {
    return {};
  }
};

const writeOverrides = (next) => {
  try {
    if (Object.keys(next).length === 0) window.localStorage.removeItem(SURFACES_STORAGE_KEY);
    else window.localStorage.setItem(SURFACES_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage blocked: the choice lasts until the page reloads.
  }
  try {
    window.dispatchEvent(new CustomEvent(SURFACES_EVENT, { detail: next }));
  } catch {
    // ignore
  }
};

/**
 * The surfaces, each { key, label, labelKey, licensed, shown }, given the overrides and a
 * licence. Pure, for tests. If nothing would be shown, the licensed surfaces
 * are (or, when none is licensed, all of them).
 */
export const resolveSurfaces = (overrides, license, keys = SURFACE_KEYS) => {
  const listed = keys
    .filter((key) => flagsPass(NAV_SECTIONS[key].requires) && productFlagPasses(key))
    .map((key) => {
      const licensed = license ? license.isSectionLicensed(key) : true;
      const choice = overrides[key];
      return {
        key, label: NAV_SECTIONS[key].label, labelKey: NAV_SECTIONS[key].labelKey, licensed, shown: typeof choice === 'boolean' ? choice : licensed,
      };
    });
  if (listed.length > 0 && !listed.some((s) => s.shown)) {
    const anyLicensed = listed.some((s) => s.licensed);
    return listed.map((s) => ({ ...s, shown: anyLicensed ? s.licensed : true }));
  }
  return listed;
};

/**
 * useSurfaceSelection() → { surfaces, isShown(key), toggle(key), reset(), customised }
 * isShown answers for surface sections only; any other section key is true.
 */
export default function useSurfaceSelection() {
  const license = useModuleLicense();
  const [overrides, setOverrides] = useState(readOverrides);

  useEffect(() => {
    const onChange = () => setOverrides(readOverrides());
    const onStorage = (e) => { if (!e || e.key === null || e.key === SURFACES_STORAGE_KEY) onChange(); };
    window.addEventListener(SURFACES_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SURFACES_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const surfaces = resolveSurfaces(overrides, license);
  const shownKeys = useMemo(
    () => new Set(surfaces.filter((s) => s.shown).map((s) => s.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [surfaces.map((s) => `${s.key}:${s.shown}`).join(',')],
  );

  const isShown = useCallback((key) => !NAV_SECTIONS[key]?.surface || shownKeys.has(key), [shownKeys]);

  const toggle = useCallback((key) => {
    const current = surfaces.find((s) => s.key === key);
    if (!current) return false;
    const next = !current.shown;
    // One surface always stays shown.
    if (!next && surfaces.filter((s) => s.shown).length <= 1) return false;
    const updated = { ...readOverrides() };
    if (next === current.licensed) delete updated[key];
    else updated[key] = next;
    writeOverrides(updated);
    setOverrides(updated);
    return true;
  }, [surfaces]);

  const reset = useCallback(() => {
    writeOverrides({});
    setOverrides({});
  }, []);

  return {
    surfaces,
    isShown,
    toggle,
    reset,
    customised: Object.keys(overrides).some((k) => SURFACE_KEYS.includes(k)),
  };
}
